import theme from "@shared/utils/themeColors";
import React, { useEffect, useRef } from "react";
import { useBubbleTutor } from "../hooks/useBubbleTutor";
import TutorMessage from "./TutorMessage";
import TutorMessageInput from "./TutorMessageInput";

interface TutorActiveConversationProps {
  conversationId: string | null;
}

/**
 * Component to display an active tutor conversation
 */
const TutorActiveConversation: React.FC<TutorActiveConversationProps> = ({
  conversationId,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const {
    currentConversation,
    messages,
    loading,
    sendingMessage,
    sendMessage,
    fetchConversation,
  } = useBubbleTutor(conversationId || undefined);

  // Scroll to bottom when messages change or when sending a message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
    // Focus on the input field
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [messages, sendingMessage]);

  // Fetch conversation when ID changes
  useEffect(() => {
    if (conversationId) {
      fetchConversation(conversationId);
    }
  }, [conversationId, fetchConversation]);

  // Handle sending a message
  const handleSendMessage = (message: string) => {
    sendMessage(message);
  };

  // Show loading state
  if (loading && !sendingMessage) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className={`text-center ${theme.text.muted}`}>
          <div
            className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.border.brand} mx-auto mb-4`}
          ></div>
          <p>Loading conversation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center">
            <div className={`text-center ${theme.text.muted}`}>
              <h3 className="text-xl font-semibold mb-2">Ask a Tutor</h3>
              <p className="mb-4">
                Get help with your test preparation, ask questions about
                concepts, or get study tips.
              </p>
              <p>Type a message below to start a conversation.</p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <TutorMessage key={message.id} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message Input */}
      <TutorMessageInput
        ref={inputRef}
        onSendMessage={handleSendMessage}
        disabled={sendingMessage}
      />
    </div>
  );
};

export default TutorActiveConversation;
